import React from "react";
import { Alert, Button, Spinner } from "react-bootstrap";
import moment from "moment";
import { useAccountHealth } from "../../hooks/useAccountHealth";
import { useBillingActions } from "../../hooks/useBillingActions";

// Map health level -> bootstrap Alert variant.
const LEVEL_VARIANT = {
  good: "success",
  warning: "warning",
  critical: "danger",
  neutral: "secondary",
};

/**
 * Plain-English account health banner for non-technical admins doing real-time
 * support. Shows a one-line summary with color, key dates, and one-click
 * "Retry Payment" when there's an open/failed invoice.
 */
const AccountHealthBanner = ({ companyId, enabled = true }) => {
  const { data: health, isLoading } = useAccountHealth(companyId, enabled);
  const { retry } = useBillingActions(companyId);

  if (isLoading) {
    return (
      <div className="mb-3 d-flex align-items-center text-muted">
        <Spinner animation="border" size="sm" className="me-2" /> Checking account health…
      </div>
    );
  }

  if (!health) return null;

  const variant = LEVEL_VARIANT[health.level] || "secondary";
  const openInvoice = health.latestOpenInvoice;

  const handleRetry = () => {
    if (
      window.confirm(
        `Retry the customer's outstanding payment${
          openInvoice?.amountDue != null ? ` of $${Number(openInvoice.amountDue).toFixed(2)}` : ""
        }? This will attempt to charge their card on file.`
      )
    ) {
      retry.mutate();
    }
  };

  return (
    <Alert variant={variant} className="mb-3">
      <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
        <div>
          <strong>{health.headline}</strong>
          <div className="small mt-1">
            {health.lastPaymentSuccessDate && (
              <span className="me-3">
                Last paid: {moment(health.lastPaymentSuccessDate).format("MMM DD, YYYY")}
              </span>
            )}
            {health.firstPaymentFailureDate && (
              <span className="me-3">
                First failure: {moment(health.firstPaymentFailureDate).format("MMM DD, YYYY")}
              </span>
            )}
            {health.waivedEndDate && (
              <span className="me-3">
                Waived until: {moment(health.waivedEndDate).format("MMM DD, YYYY")}
              </span>
            )}
            {openInvoice?.amountDue != null && (
              <span className="me-3">
                Amount due: ${Number(openInvoice.amountDue).toFixed(2)}
              </span>
            )}
          </div>
        </div>
        {health.canRetryPayment && (
          <div className="d-flex gap-2">
            {openInvoice?.hostedUrl && (
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => window.open(openInvoice.hostedUrl, "_blank")}
              >
                View Invoice
              </Button>
            )}
            <Button
              variant="primary"
              size="sm"
              onClick={handleRetry}
              disabled={retry.isPending}
            >
              {retry.isPending ? "Retrying…" : "Retry Payment"}
            </Button>
          </div>
        )}
      </div>
    </Alert>
  );
};

export default AccountHealthBanner;
